<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { get } from 'svelte/store';
	import { toast } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import EmptyElementMsgAndBtn from './EmptyElementMsgAndBtn.svelte';
	import DoPreviewWithControls from './components/DoPreviewWithControls.svelte';
	import { _deepClone, _deepCopyArr, deepCopy } from '../../../../helpers/utilities';
	import {
		add_action,
		delete_actions_from_thens,
		edit_action,
		update_scene_with_new_thens,
	} from '../manage_scene_elements';
	import {
		createDeviceAction,
		createNotificationAction,
		createSceneAction,
		createGroupAction,
	} from '../../helpers/ui_actions_to_scene';
	import { get_mode, real_id } from '../../../../helpers/interface_helpers';
	import { isXT1Module } from '../../../../helpers/xt1_helpers/template_helpers';
	import type { ISceneObj } from '../../../../types/SceneType';
	import AddEditActionWizardV2 from '../Process/Do/АddEditActionWizard/AddEditActionWizardV2.svelte';
	import ImportActionsModal from '../Process/ImportFromSceneModal/ImportActionsModal.svelte';
	import ActionTypeSelectionModal from '../Process/SelectTypeModal/ActionTypeSelectionModal.svelte';
	import { actionInterpretator } from '../../helpers/scene_actions_to_ui';
	import { addRemoveDeviceIdsFromMetaInSceneObject } from '../Process/GenericComponents/helpers';
	import { getSceneActions } from '../../helpers/scene_data_helpers';

	export let scene_object: ISceneObj;
	export let hideActions = false;
	export let showAddEditAction = false;
	export let do_info = { mode: 'add', type: 'action', ind: -1 };
	export let element = null;
	export let stopConfirmationModal = false;
	export let backdropCloseModal = false;

	let showTypeSelectionModal = false;
	let showImportModal = false;

	const dispatch = createEventDispatcher();

	let thens = getSceneActions(scene_object);

	const createTranslated = (el, doMode) => {
		/**
		 * First we need to extract the real action from an delay action if it is a delayed one
		 */
		if (element?.do !== undefined && element?.wait !== undefined) {
			//if there is do => it is a delay action, stupid, but in other way we miss it
			let copyDelay = { delay: element.wait, units: element.units };
			el = { ...element.do.blk[0], ...copyDelay };
		}

		let msg_type = el?.msg_type;
		let purpose =
			el?._gui_type ||
			el?._gui_purpose ||
			el?.do?.blk[0]?._gui_type ||
			el?.do?.blk[0]?._gui_purpose ||
			undefined;

		return doMode === 'add' ? undefined : actionInterpretator(purpose, el, msg_type);
	};

	const defaultSceneData = {
		scene: element,
		scene_object: scene_object,
		do_info,
		selectedAction: 1,
		translated: undefined,
		selectedDevice: {},
		selectedGroup: {},
		selectedScene: {},
		deviceAction: {
			device: {
				id: undefined,
				channel: '0',
			},
			actions: [],
			delay: {
				units: 'sec',
				value: 0,
			},
			timer: {
				units: 'sec',
				value: 0,
			},
			components: [],
		},
		notificationAction: {
			id: -1,
			name: '',
			msg: $format('notification_msg', { default: 'My scene triggered' }),
			msg_type: '',
			type: '',
			delay: {
				value: 0,
				units: 'sec',
			},
			already_in_current_scene: scene_object?.do?.find((_do) => {
				return _do.notify === 'alexa_notif' || (_do.blk && _do.blk[0].notify === 'alexa_notif');
			}),
		},
		groupAction: {
			actions: [],
			components: [],
			delay: {
				value: 0,
				units: 'sec',
			},
		},
		sceneAction: {
			option: undefined,
			delay: {
				value: 0,
				units: 'sec',
			},
		},
		alarmAction: {
			option: undefined,
			delay: {
				value: 0,
				units: 'sec',
			},
		},
		isValid: false,
		validStepIndex: 0,
	};
	let sceneData = _deepClone(defaultSceneData);

	const handle_delete = (e) => {
		do_info = { ...e.detail, mode: 'delete' };

		let _copy_thens = _deepCopyArr(thens);

		thens = delete_actions_from_thens(_copy_thens, do_info.ind);

		scene_object = addRemoveDeviceIdsFromMetaInSceneObject(
			'action',
			_copy_thens?.[do_info.ind],
			scene_object,
		);

		// let _scene_copy = _deepClone($scene_object);
		// let _sc = update_scene_with_new_thens(_scene_copy, thens);
		// scene_object.change(_sc);
		scene_object = update_scene_with_new_thens(scene_object, thens);
		console.log('delete actions updated ', JSON.stringify(scene_object.do));
		do_info = { mode: 'add', type: 'action', ind: -1 };
	};

	const handle_edit = (e) => {
		showAddEditAction = true;
		do_info = { ...e.detail, mode: 'edit' };
		element = do_info.ind >= 0 ? thens[do_info.ind] : undefined;

		scene_object = addRemoveDeviceIdsFromMetaInSceneObject('action', element, scene_object);

		// Old implementation footer should not be hidden in the new flow
		// dispatch('hide_footer', { hideFooter: showAddEditAction });
	};
	const handle_add = (e) => {
		do_info = { ...e.detail, mode: 'add' };
		element = undefined;
		showTypeSelectionModal = true;
		// Old implementation footer should not be hidden in the new flow
		// dispatch('hide_footer', { hideFooter: showAddEditAction });
	};

	const handle_add_first = () => {
		do_info = { mode: 'add', type: 'action', ind: -1 };
		element = undefined;
		showTypeSelectionModal = true;
		// Old implementation footer should not be hidden in the new flow
		// dispatch('hide_footer', { hideFooter: showAddEditAction });
	};

	const handle_cancel = () => {
		do_info = { mode: 'add', ind: -1, type: 'action' };
		showAddEditAction = false;
		// Old implementation footer should not be hidden in the new flow
		// dispatch('hide_footer', { hideFooter: showAddEditAction });
	};

	const handle_save = (e) => {
		// console.log(
		// 	'we will try to create and save new action with data ',
		// 	e.detail
		// );
		let new_el: object | undefined;
		const { t_data, s_type, do_info } = e.detail;
		switch (s_type) {
			case 'device_action':
				let dev_id =
					Number(t_data.device.channel) > 0
						? `${t_data.device.id}_${t_data.device.channel}`
						: t_data.device.id;
				let settings = get(getDeviceSettings(dev_id));
				let status = get(getDeviceStatus(dev_id));
				const device = $devices[dev_id];

				if (scene_object._meta.hasOwnProperty('adi') && Array.isArray(scene_object._meta.adi)) {
					if (!scene_object._meta.adi.includes(dev_id)) {
						scene_object._meta.adi.push(dev_id);
					}
				} else {
					scene_object._meta = {
						...scene_object._meta,
						adi: [dev_id],
					};
				}

				let mode;
				// XT devices can get their mode without status/settings via getTemplateMode
				// For other devices, we need status and settings to determine mode
				if (isXT1Module(device?.type)) {
					mode = get_mode(device, status, settings);
					// Fallback to 'module' if getTemplateMode fails (e.g., settings not loaded)
					if (!mode) {
						mode = 'module';
					}
				} else if (settings && status) {
					mode = get_mode(device, status, settings);
				}

				new_el = createDeviceAction(t_data, device?.type, mode);
				break;
			case 'notify_action':
				new_el = createNotificationAction(t_data);
				// console.warn('New notification ', JSON.stringify(new_el, null, 2));
				break;
			case 'automation_action':
				new_el = createSceneAction(t_data);
				console.log('new automation action');
				break;
			case 'group_action':
				console.log(t_data, s_type, do_info);
				new_el = createGroupAction(t_data);

				break;
			default:
				break;
		}

		if (typeof new_el === 'undefined') {
			toast.open({
				type: 'danger',
				title: $format('scene_invalid_format_err_toast'),
			});
			return;
		}
		//update scene_object
		// let _scene = {};
		//save new el on pos
		switch (do_info.mode) {
			case 'add':
				scene_object = add_action(scene_object, new_el);
				break;
			case 'edit':
				scene_object = edit_action(scene_object, new_el, do_info.ind);
				break;
			default:
				toast.open({
					type: 'danger',
					title: $format('operation_failed', { default: 'Operation failed' }),
				});
				return;
		}

		// scene_object.change(_scene);
		showAddEditAction = false;
		// console.warn('SCENE OBJ CHANGE ', JSON.stringify($scene_object, null, 2));
		thens = getSceneActions(scene_object);
		// Old implementation footer should not be hidden in the new flow
		// dispatch('hide_footer', { hideFooter: showAddEditAction });

		dispatch('save');
		onCloseAddEditActionWizard();
	};

	const resetSceneData = () => {
		sceneData = deepCopy({ ...defaultSceneData });
	};

	const onCloseAddEditActionWizard = () => {
		showAddEditAction = false;
		resetSceneData();
	};

	const handleImportActions = (e) => {
		const { items } = e.detail;

		// Import multiple actions - each action is added to the 'do' array sequentially
		items.forEach((importedItem) => {
			const action = importedItem.item;

			// Add action to scene
			scene_object = add_action(scene_object, action);

			// Handle device IDs in metadata
			if (!action.id) return;

			const dev_id = action.id;
			if (!scene_object._meta.hasOwnProperty('adi') || !Array.isArray(scene_object._meta.adi)) {
				scene_object._meta = {
					...scene_object._meta,
					adi: [dev_id],
				};
				return;
			}

			if (!scene_object._meta.adi.includes(dev_id)) {
				scene_object._meta.adi.push(dev_id);
			}
		});

		// Update thens display
		thens = getSceneActions(scene_object);

		dispatch('save');

		toast.open({
			type: 'success',
			title: $format('actions_imported_successfully', {
				default: 'Actions imported successfully',
			}),
		});
	};

	$: translated = createTranslated(element, do_info.mode);

	$: {
		if (sceneData.scene !== element) {
			sceneData.scene = element;
		}
		if (sceneData.do_info !== do_info) {
			sceneData.do_info = do_info;
		}
		if (sceneData.translated !== translated) {
			sceneData.translated = translated;
		}
	}
</script>

{#if !hideActions}
	{#if thens.length > 0}
		<!--
			display in row view (translated sdr to ui)
		-->
		<DoPreviewWithControls
			bind:thens
			on:delete={(e) => handle_delete(e)}
			on:edit={(e) => handle_edit(e)}
			on:add={(e) => handle_add(e)}
		/>
	{:else}
		<!-- Missing condition + Add button-->
		<EmptyElementMsgAndBtn type={'action'} on:click={(e) => handle_add_first()} />
	{/if}
{/if}

{#if showAddEditAction}
	<AddEditActionWizardV2
		showWizard={showAddEditAction}
		backdropClose={backdropCloseModal}
		{stopConfirmationModal}
		bind:sceneData
		on:save={(e) => handle_save(e)}
		on:close={onCloseAddEditActionWizard}
		on:back={() => {
			showAddEditAction = false;
			showTypeSelectionModal = true;
		}}
	/>
{/if}

{#if showTypeSelectionModal}
	<ActionTypeSelectionModal
		bind:showModal={showTypeSelectionModal}
		on:create={() => {
			showTypeSelectionModal = false;
			showAddEditAction = true;
		}}
		on:import={() => {
			showTypeSelectionModal = false;
			showImportModal = true;
		}}
		on:close={() => {
			showTypeSelectionModal = false;
		}}
	/>
{/if}

{#if showImportModal}
	<ImportActionsModal
		bind:showModal={showImportModal}
		currentSceneId={scene_object._id}
		on:import={handleImportActions}
		on:close={() => {
			showImportModal = false;
			showTypeSelectionModal = true;
		}}
	/>
{/if}
